import { FC, useCallback, useEffect, useState } from "react"
import { Formik, FormikHelpers } from "formik"
import { WasmRunner } from "@coherentglobal/wasm-runner"
import { ResponseObject } from "@coherentglobal/wasm-runner/dist/types"

import { FormValues, PayloadType } from "../../types"
import { InputForm } from "./InputForm"
import { formatFormValues, formatRequestData, prepareJsonDataForRequest } from "../../utils/dataFormat"
import { InputJson } from "./InputJson"
import { prepareEditableJsonData } from "../../utils/dataFormat"
import useFormValuesStore from "../../store/useFormValuesStore"

export type InputTabProps = {
  /**
   * Save the response so it can be used in the output tab
   */
  setResponse: (value: ResponseObject | { error: any }) => void
  /**
   * Function used to switch between tabs
   */
  setTabIndex: (value: number) => void
  /**
   * Wasm runner initializer function
   */
  initWasmRunner: () => void
  /**
   * Wasm runner
   */
  runner: WasmRunner
  /**
   * response data
   */
  response: any
}

export const InputTab: FC<InputTabProps> = ({ setResponse, setTabIndex, initWasmRunner, runner, response }) => {
  const [jsonView, setJsonView] = useState<boolean>(false)
  const [editableModelData, setEditableModelData] = useState(prepareEditableJsonData())
  const { setValues, values: storeValues } = useFormValuesStore((state) => state)

  const runCalculation = useCallback(
    async (payload: PayloadType) => {
      setValues(payload)
      if (runner) {
        return await runner.execute(payload).catch((err) => ({ error: err.v3 }))
      } else {
        await initWasmRunner()
        throw Error("Runner not initialized. Please retry.")
      }
    },
    [initWasmRunner, runner, setValues]
  )

  const calculate = async (values: any) => {
    const response = await runCalculation(formatRequestData(values))
    setResponse(response)
    setTabIndex(1)
  }

  const calculateJSON = async (values: any) => {
    const requestValues = prepareJsonDataForRequest(values)
    const response = await runCalculation(requestValues)
    setResponse(response)
    setTabIndex(1)
  }

  // workround, if there is no reponse data at first, send requests till u get response data
  useEffect(() => {
    let interval: NodeJS.Timer | undefined
    if (!response?.response_data?.outputs?.PDC) {
      if (storeValues) {
        interval = setInterval(() => {
          runCalculation(storeValues)
        }, 1000)
      }
    } else {
      if (interval) {
        clearInterval(interval)
      }
    }
    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [response, runCalculation, storeValues])

  return (
    <>
      <div className="h-24 w-full flex items-center justify-center">
        <h1 className="w-full max-w-xs text-center text-black text-3xl font-semibold">
          Expected Credit Losses Calculator
        </h1>
      </div>

      <div className="flex mx-auto w-full max-w-sm space-x-2">
        <button
          className={`${
            jsonView ? "bg-form-dark text-black" : "bg-brand-light text-white"
          } rounded-l py-2 px-8 text-sm w-1/2`}
          type="button"
          onClick={() => setJsonView(false)}
        >
          FIELDS
        </button>
        <button
          className={`${
            jsonView ? "bg-brand-light text-white" : "bg-form-dark  text-black"
          } rounded-r py-2 px-8 text-sm w-1/2`}
          type="button"
          onClick={() => setJsonView(true)}
        >
          JSON VIEW
        </button>
      </div>

      <Formik
        initialValues={{
          Segment: { id: 2, value: editableModelData.request_data.inputs.Segment },
          ObligorRating: { id: 1, value: editableModelData.request_data.inputs.ObligorRating },
          LGD: editableModelData.request_data.inputs.LGD,
          RecoveryLags: editableModelData.request_data.inputs.RecoveryLags,
          Stage_Regular: { id: 2, value: editableModelData.request_data.inputs.Stage_Regular },
          CashFlowTypes: { id: 1, value: editableModelData.request_data.inputs.CashFlowTypes },
          PaymentFreq: { id: 4, value: editableModelData.request_data.inputs.PaymentFreq },
          AssessmentDate: editableModelData.request_data.inputs.AssessmentDate,
          NextPaymentDate: editableModelData.request_data.inputs.NextPaymentDate,
          OutstandingBalance: editableModelData.request_data.inputs.OutstandingBalance,
          LoanLimit: editableModelData.request_data.inputs.LoanLimit,
          CCF: editableModelData.request_data.inputs.CCF,
          Contract_IR: editableModelData.request_data.inputs.Contract_IR,
        }}
        //@ts-ignore
        onSubmit={(values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
          const formattedValues = formatFormValues(values)
          calculate(formattedValues)
        }}
      >
        {({ errors, touched, handleChange, setFieldValue }) =>
          jsonView ? (
            <InputJson
              calculate={calculateJSON}
              defaultValues={editableModelData}
              setDefaultFormValues={setEditableModelData}
            />
          ) : (
            <InputForm errors={errors} touched={touched} handleChange={handleChange} setFieldValue={setFieldValue} />
          )
        }
      </Formik>
    </>
  )
}

